import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Brain, CheckCircle2, XCircle, AlertTriangle,
  Zap, Scale, DollarSign, Target, ChevronDown, ChevronUp, FileText, ExternalLink,
} from 'lucide-react';

// Helper — render flag value (string lub {text, citation})
function FlagItem({ flag }) {
  if (typeof flag === 'string') {
    return <li className="line-clamp-2">{flag}</li>;
  }
  if (flag && typeof flag === 'object') {
    const text = flag.text || flag.description || JSON.stringify(flag);
    const cit = flag.citation;
    return (
      <li className="space-y-0.5">
        <div className="flex items-start gap-2">
          <span className="flex-1 line-clamp-2">{text}</span>
          {cit?.pdf_link && (
            <a
              href={cit.pdf_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-medium shrink-0"
              title={cit.attachment_filename || 'Otwórz PDF'}
              onClick={(e) => e.stopPropagation()}
            >
              <FileText className="w-2.5 h-2.5" />
              s.{cit.page_no}
              <ExternalLink className="w-2 h-2" />
            </a>
          )}
        </div>
        {cit?.excerpt && (
          <div className="ml-3 text-[10px] italic text-muted-foreground/80 border-l-2 border-primary/30 pl-2 line-clamp-2">
            „{cit.excerpt}"
          </div>
        )}
      </li>
    );
  }
  return <li>{String(flag)}</li>;
}
import { runTenderConsilium, listConsiliumTypes } from '../../lib/api';

const MODEL_ICONS = {
  anthropic: '🅐',
  openai: '🅞',
  gemini: '🅖',
};

const ANALYSIS_ICONS = {
  red_team: AlertTriangle,
  blue_team: Zap,
  pricing: DollarSign,
  legal: Scale,
  compete: Target,
};

const ANALYSIS_COLORS = {
  red_team: 'text-red-400 border-red-500/30',
  blue_team: 'text-emerald-400 border-emerald-500/30',
  pricing: 'text-amber-400 border-amber-500/30',
  legal: 'text-blue-400 border-blue-500/30',
  compete: 'text-purple-400 border-purple-500/30',
};

const VERDICT_STYLES = {
  start: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  negotiate: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  skip: 'bg-red-500/15 text-red-400 border-red-500/30',
  kio: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  kio_challenge: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  kio_appeal: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  partner: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  compliant: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  contest: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

function ModelCard({ result }) {
  const [expanded, setExpanded] = useState(false);
  const isOk = result.status === 'ok';
  const res = result.result || {};
  const icon = MODEL_ICONS[result.provider] || '●';

  return (
    <Card className={`${isOk ? '' : 'opacity-60'}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="text-lg">{icon}</span>
              <span className="truncate">{result.model}</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{result.role}</p>
          </div>
          <div className="flex items-center gap-2">
            {isOk ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            ) : (
              <XCircle className="w-4 h-4 text-red-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {result.latency_ms ? `${(result.latency_ms / 1000).toFixed(1)}s` : ''}
            </span>
          </div>
        </div>
      </CardHeader>
      {isOk && (
        <CardContent className="pt-0 pb-3">
          {/* Key metrics per type */}
          <div className="space-y-1.5 text-xs">
            {res.risk_score != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ryzyko:</span>
                <span className="font-medium">{res.risk_score}/100</span>
              </div>
            )}
            {res.opportunity_score != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Szansa:</span>
                <span className="font-medium">{res.opportunity_score}/100</span>
              </div>
            )}
            {res.compliance_score != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Zgodność PZP:</span>
                <span className="font-medium">{res.compliance_score}/100</span>
              </div>
            )}
            {res.suggested_bid != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sugerowana cena:</span>
                <span className="font-medium">{Number(res.suggested_bid).toLocaleString('pl-PL')} PLN</span>
              </div>
            )}
            {res.dumping_risk != null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Dumping:</span>
                <span className="font-medium">{res.dumping_risk}/100</span>
              </div>
            )}
            {res.recommendation && (
              <div className="flex items-center gap-2 pt-1">
                <Badge variant="outline" className={VERDICT_STYLES[res.recommendation] || ''}>
                  {res.recommendation}
                </Badge>
              </div>
            )}
          </div>
          {res.reasoning && (
            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
              {res.reasoning}
            </div>
          )}
          {(res.red_flags?.length > 0 || res.violations?.length > 0 || res.strengths?.length > 0) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full flex items-center justify-between text-xs text-primary/80 mt-2"
            >
              <span>{expanded ? 'Zwiń' : 'Szczegóły'}</span>
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          {expanded && (
            <div className="text-xs space-y-2 mt-2">
              {res.red_flags?.length > 0 && (
                <div>
                  <div className="font-medium text-red-400 mb-1">Red flags:</div>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    {res.red_flags.slice(0, 5).map((f, i) => <FlagItem key={i} flag={f} />)}
                  </ul>
                </div>
              )}
              {res.favoring_indicators?.length > 0 && (
                <div>
                  <div className="font-medium text-amber-400 mb-1">Faworyzowanie:</div>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    {res.favoring_indicators.slice(0, 5).map((f, i) => <FlagItem key={i} flag={f} />)}
                  </ul>
                </div>
              )}
              {res.strengths?.length > 0 && (
                <div>
                  <div className="font-medium text-emerald-400 mb-1">Mocne strony:</div>
                  <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                    {res.strengths.slice(0, 5).map((f, i) => <li key={i} className="line-clamp-2">{f}</li>)}
                  </ul>
                </div>
              )}
              {res.violations?.length > 0 && (
                <div>
                  <div className="font-medium text-amber-400 mb-1">Naruszenia:</div>
                  <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                    {res.violations.slice(0, 3).map((f, i) => (
                      <li key={i} className="line-clamp-2">{typeof f === 'object' ? `${f.article}: ${f.description}` : f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
      {!isOk && (
        <CardContent className="pt-0 pb-3">
          <p className="text-xs text-red-400">{result.error || result.status}</p>
        </CardContent>
      )}
    </Card>
  );
}

function ArbiterVerdict({ arbiter }) {
  if (!arbiter || arbiter.status !== 'ok') return null;
  const r = arbiter.result || {};
  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            Arbiter — werdykt
          </CardTitle>
          <Badge variant="outline" className={VERDICT_STYLES[r.final_verdict] || ''}>
            {r.final_verdict?.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          {r.overall_risk != null && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Ryzyko łączne</div>
              <Progress value={r.overall_risk} className="h-2" />
              <div className="text-sm font-medium mt-1">{r.overall_risk}/100</div>
            </div>
          )}
          {r.overall_opportunity != null && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Szansa łączna</div>
              <Progress value={r.overall_opportunity} className="h-2" />
              <div className="text-sm font-medium mt-1">{r.overall_opportunity}/100</div>
            </div>
          )}
          {r.confidence != null && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Pewność</div>
              <Progress value={Math.round((r.confidence || 0) * 100)} className="h-2" />
              <div className="text-sm font-medium mt-1">
                {Math.round((r.confidence || 0) * 100)}%
              </div>
            </div>
          )}
        </div>

        {r.consensus_strength && (
          <Badge
            variant="outline"
            className={
              r.consensus_strength === 'strong' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                : r.consensus_strength === 'moderate' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                : 'bg-red-500/10 text-red-400 border-red-500/30'
            }
          >
            Konsensus: {r.consensus_strength}
          </Badge>
        )}

        {r.key_insights?.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-1">Kluczowe wnioski:</div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {r.key_insights.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          </div>
        )}

        {r.disagreements?.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-1 text-amber-400">Rozbieżności w panelu:</div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              {r.disagreements.map((i, idx) => <li key={idx}>{i}</li>)}
            </ul>
          </div>
        )}

        {r.action_items?.length > 0 && (
          <div>
            <div className="text-sm font-medium mb-2">Rekomendowane działania:</div>
            <div className="space-y-1.5">
              {r.action_items.map((a, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <Badge variant="outline" className={
                    a.priority === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/30 shrink-0 mt-0.5' :
                    a.priority === 'medium' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30 shrink-0 mt-0.5' :
                    'bg-muted shrink-0 mt-0.5'
                  }>
                    {a.priority}
                  </Badge>
                  <span className="text-muted-foreground">{typeof a === 'object' ? a.action : a}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ConsiliumPanel({ tenderId, tenderTitle }) {
  const [analysisType, setAnalysisType] = useState('red_team');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [types, setTypes] = useState({});

  useEffect(() => {
    listConsiliumTypes().then(setTypes).catch(() => {});
  }, []);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      toast.info(`Uruchamiam panel ${Object.keys(types).length || 5} modeli AI...`);
      const r = await runTenderConsilium(tenderId, analysisType);
      setResult(r);
      toast.success(`Analiza ukończona (${r.successful_count}/${r.panel_size} modeli)`);
    } catch (e) {
      toast.error('Consilium error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const typeKeys = Object.keys(types).length ? Object.keys(types)
    : ['red_team', 'blue_team', 'pricing', 'legal', 'compete'];

  return (
    <div className="space-y-4">
      <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Brain className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Consilium AI — panel 5 modeli</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Claude Sonnet + GPT-4o + Gemini + Claude Haiku + GPT-4o-mini analizują równolegle.
                Arbiter syntetyzuje werdykt. Czas: 15-30s.
              </p>
              <Tabs value={analysisType} onValueChange={setAnalysisType} className="mt-3">
                <TabsList className="grid grid-cols-5 w-full">
                  {typeKeys.map(k => {
                    const Icon = ANALYSIS_ICONS[k] || Sparkles;
                    return (
                      <TabsTrigger key={k} value={k} className="text-xs">
                        <Icon className="w-3.5 h-3.5 mr-1.5" />
                        {types[k] ? types[k].replace(/ —.*/, '') : k}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>
            <Button onClick={handleRun} disabled={loading} size="lg">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Uruchom Consilium
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="pt-5 pb-5 space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm">Modele analizują SWZ równolegle...</span>
            </div>
            <Progress value={undefined} className="h-1" />
            <p className="text-xs text-muted-foreground">
              To może potrwać 15-30 sekund. 5 modeli z 3 firm (Anthropic, OpenAI, Google) pracuje równolegle.
            </p>
          </CardContent>
        </Card>
      )}

      {result && (
        <>
          {/* Arbiter */}
          <ArbiterVerdict arbiter={result.arbiter_verdict} />

          {/* Consensus + stats */}
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="text-muted-foreground">Zgodność panelu:</span>
                  <span className="font-medium ml-2">
                    {Math.round((result.consensus_score || 0) * 100)}%
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Modele:</span>
                  <span className="font-medium ml-2">
                    {result.successful_count}/{result.panel_size}
                  </span>
                </div>
                <Badge variant="outline" className={ANALYSIS_COLORS[result.analysis_type]}>
                  {result.analysis_name}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Panel results grid */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {result.by_model.map((r, i) => <ModelCard key={i} result={r} />)}
          </div>
        </>
      )}
    </div>
  );
}
